import { useMemo } from 'react';
import { AmlHistoryItemData } from '@/redux/models';
import { useTranslation } from 'react-i18next';
import { RiskDetailsCategory } from '@/components';
import { CounterpartySignals } from '../CounterpartySignals';
import { CounterpartyConnections } from '../CounterpartyConnections';
import { getRiskGroups, getRiskByGroup } from '@/helpers';
import styles from './AddressSignals.module.sass';

interface AddressSignalsProps {
  item: AmlHistoryItemData;
}

const AddressSignals = ({ item }: AddressSignalsProps) => {
  const { t } = useTranslation();
  const groups = getRiskGroups(item.signals, item.flow);

  const showCounterpartyData = useMemo(() => {
    return (
      !!item.counterparty &&
      !item.counterparty?.name &&
      !!item.counterparty?.signals
    );
  }, [item]);

  const emptyConnections = useMemo(() => {
    return (
      !Object.keys(groups).length &&
      !item?.connections?.length &&
      (!item?.counterparty || !item.counterparty?.connections?.length)
    );
  }, [item, groups]);

  const hasCounterpartySignals = useMemo(() => {
    if (!item.counterparty?.signals) {
      return false;
    }

    const incomingPercent: number = (
      Object.values(item.counterparty?.signals.in) as number[]
    ).reduce((acc, val) => acc + val, 0);

    if (incomingPercent > 0) return true;

    const outgoingPercent: number = (
      Object.values(item.counterparty?.signals.out) as number[]
    ).reduce((acc, val) => acc + val, 0);

    return outgoingPercent > 0;
  }, [item]);

  const groupFigures = useMemo(() => getRiskByGroup(groups), [groups]);

  return (
    <div className={styles.details}>
      {showCounterpartyData && hasCounterpartySignals && (
        <CounterpartySignals
          flow={item.flow}
          counterparty={item.counterparty}
        />
      )}

      <div className={styles.title}>
        {emptyConnections
          ? t('translation.str_addr_connections_empty_msg')
          : t('translation.wallet_connection_title')}
      </div>

      <div className={styles.list}>
        {Object.keys(groups).map((category) => (
          <div key={category}>
            <RiskDetailsCategory
              category={category}
              risks={groups[category]}
              percentage={groupFigures[category]}
            />
          </div>
        ))}
      </div>

      {showCounterpartyData && (
        <CounterpartyConnections
          connections={item.counterparty?.connections}
          tooltip={t('translation.entities_tooltip_address')}
        />
      )}
    </div>
  );
};

export default AddressSignals;
